import type { CSSProperties } from 'react';
import { Building2 } from 'lucide-react';
import { Link } from 'react-router';
import type { DashboardOccupancy, DashboardUnitState } from '@brimostech/shared';
import { Button, cn, EmptyState } from '@brimostech/ui';
import { Section } from '@/components/section';

/**
 * Two categorical hues for the map, validated for colour-blind separation (ΔE 22 deutan) on
 * the light and the dark surface; "vazia" is the absence of a fill, so it reads without colour.
 */
const COLORS = { '--occ-rented': '#3056d3', '--occ-owner': '#2a9d8f' } as CSSProperties;

const STATES: Record<DashboardUnitState, { one: string; many: string; fill: string }> = {
  rented: { one: 'alugada', many: 'alugadas', fill: 'bg-[var(--occ-rented)]' },
  owner: { one: 'própria', many: 'próprias', fill: 'bg-[var(--occ-owner)]' },
  vacant: { one: 'vazia', many: 'vazias', fill: 'border border-dashed border-foreground/40' },
};
const ORDER: DashboardUnitState[] = ['rented', 'owner', 'vacant'];

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The building as a map (ADR-111): one tile per unit, grouped by block, filled by who lives
 * there — a tenant, the owner's household, or nobody. The legend carries the totals.
 */
export function OccupancySection({
  occupancy,
  canRegistry,
}: {
  occupancy: DashboardOccupancy;
  canRegistry: boolean;
}) {
  const occupied = occupancy.rented + occupancy.ownerOccupied;
  const percent = occupancy.units === 0 ? 0 : Math.round((occupied / occupancy.units) * 100);
  const count = (state: DashboardUnitState) =>
    state === 'rented'
      ? occupancy.rented
      : state === 'owner'
        ? occupancy.ownerOccupied
        : occupancy.vacant;
  return (
    <Section
      title="Ocupação"
      description="Cada quadrado é uma unidade: quem mora nela hoje."
      actions={
        canRegistry ? (
          <Button variant="ghost" size="sm" asChild>
            <Link to="/unidades">Unidades</Link>
          </Button>
        ) : undefined
      }
    >
      {occupancy.units === 0 ? (
        <EmptyState
          icon={<Building2 />}
          title="Nenhuma unidade cadastrada"
          description="Cadastre os blocos e as unidades para ver a ocupação do condomínio."
          action={
            canRegistry ? (
              <Button asChild>
                <Link to="/unidades">Cadastrar unidades</Link>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-5" style={COLORS}>
          <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
            <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              {ORDER.map((state) => {
                const n = count(state);
                return (
                  <li
                    key={state}
                    aria-label={plural(
                      n,
                      `unidade ${STATES[state].one}`,
                      `unidades ${STATES[state].many}`,
                    )}
                    className="flex items-center gap-2"
                  >
                    <span aria-hidden className={cn('size-3 rounded-[3px]', STATES[state].fill)} />
                    <span className="text-xl font-semibold tabular-nums">{n}</span>
                    <span className="text-muted-foreground">
                      {n === 1 ? STATES[state].one : STATES[state].many}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="text-right text-sm text-muted-foreground">
              <span className="block text-xl font-semibold tabular-nums text-foreground">
                {percent}% ocupado
              </span>
              <span>
                {plural(occupancy.residents, 'morador', 'moradores')} ·{' '}
                {plural(occupancy.vehicles, 'veículo', 'veículos')}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {occupancy.blocks.map((block) => (
              <div key={block.id} className="flex flex-wrap items-start gap-x-4 gap-y-2">
                {block.name ? (
                  <span className="w-24 shrink-0 truncate pt-0.5 text-xs font-medium text-muted-foreground">
                    {block.name}
                  </span>
                ) : null}
                <ul className="flex flex-wrap gap-1.5">
                  {block.units.map((unit) => {
                    const label = block.name ? `${block.name} ${unit.number}` : unit.number;
                    const title = `${label} · ${STATES[unit.state].one}${
                      unit.residents > 0
                        ? ` · ${plural(unit.residents, 'morador', 'moradores')}`
                        : ''
                    }`;
                    const className = cn(
                      'block size-5 rounded-[4px] transition-transform hover:scale-125 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                      STATES[unit.state].fill,
                    );
                    return (
                      <li key={unit.id}>
                        {canRegistry ? (
                          <Link
                            to={`/unidades/${unit.id}`}
                            aria-label={`${label}: ${STATES[unit.state].one}`}
                            title={title}
                            className={className}
                          />
                        ) : (
                          <span
                            aria-label={`${label}: ${STATES[unit.state].one}`}
                            title={title}
                            className={className}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}
